import React, { useEffect, useState } from "react"
import {
  Box,
  Grid,
  useTheme,
  Popover,
  Checkbox,
  Typography,
  InputAdornment,
  TextField
} from "@mui/material"
import { toast } from "react-toastify"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import SearchIcon from "@mui/icons-material/Search"
import tuneIcon from "assets/icons/tuneIcon.svg"
import reloadIcon from "assets/icons/reloadIcon.svg"
import { useDispatch, useSelector } from "react-redux"
import { getGroupedAnalyticsAction, exportGroupedAnalyticsAction } from "redux/DMV/actions"
import { DMVActions } from "redux/DMV/slice/user"
import AnalyticsDateRangeFilter from "./component/AnalyticsDateRangeFilter"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import AnalyticsTable from "./component/analyticsTable"
import { metricsOptions } from "helpers/constant"
import { getUserRolesAction } from "redux/DMV/actions/user"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import AppPagination from "components/StyledComponents/AppPagination"
import styles from "./groupAnalytics.module.scss"
import { appActions } from "redux/app/slice/app"
import AppTextField from "components/StyledComponents/AppTextField"

const GroupedAnalytics = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedMetrics, setSelectedMetrics] = useState([])
  const [appliedMetrics, setAppliedMetrics] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [dateAnchorEl, setDateAnchorEl] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [awolDays, setAwolDays] = useState(7)
  const { groupedAnalytics, groupedAnalyticsLoading } = useSelector((store) => store.dmv)
  const currentFilters = useSelector((store) => store.app.filters || {})
  const {
    startDate,
    endDate,
    startDate2,
    endDate2,
    isCompare,
    raffleAccess,
    employeeClass,
    userStatus,
    platformUser,
    restaurant,
    usersLoginStatus
  } = currentFilters

  useEffect(() => {
    dispatch(DMVActions.initGroupedAnalytics())
    setBreadcrumbs(createBreadcrumbs())
    dispatch(appActions.initFilters())
    dispatch(getUserRolesAction({}))
    setCurrentPage(1)
    document.title = "Dashboard - Grouped Analytics"
  }, [])

  const handleMetricsClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  useEffect(() => {
    dispatch(getUserRolesAction({ restaurant: restaurant }))
    const payload = []
    payload["employeeClass"] = "ALL"
    dispatch(appActions.setFilters(payload))
  }, [restaurant])

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleDateClick = (event) => {
    setDateAnchorEl(event.currentTarget)
  }

  const handleDateClose = () => {
    setDateAnchorEl(null)
  }

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedMetrics(metricsOptions?.map((m) => m?.value))
    } else {
      setSelectedMetrics([])
    }
  }

  const handleMetricToggle = (value) => {
    setSelectedMetrics((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    )
  }

  const handleGenerateReport = () => {
    if (!startDate || !endDate) {
      toast.error("Please select a date range", {
        position: "bottom-left"
      })
      return
    }

    if (isCompare && (!startDate2 || !endDate2)) {
      toast.error("Please select a comparison date range", {
        position: "bottom-left"
      })
      return
    }

    if (!selectedMetrics || selectedMetrics.length === 0) {
      toast.error("Please select at least one metric", {
        position: "bottom-left"
      })

      return
    }

    setAppliedMetrics(selectedMetrics)
    if (!isCompare) {
      dispatch(
        appActions.setFilters({
          ...currentFilters,
          startDate2: "",
          endDate2: ""
        })
      )
    }
    dispatch(
      getGroupedAnalyticsAction({
        startDate,
        endDate,
        startDate2: isCompare ? startDate2 : "",
        endDate2: isCompare ? endDate2 : "",
        metrics: selectedMetrics,
        raffleAccess,
        employeeClass,
        userStatus,
        awolDays,
        platformUser,
        restaurant,
        usersLoginStatus
      })
    )
  }

  const handleExport = () => {
    if (!startDate || !endDate) {
      toast.error("Please select a date range", {
        position: "bottom-left"
      })
      return
    }

    if (isCompare && (!startDate2 || !endDate2)) {
      toast.error("Please select a comparison date range", {
        position: "bottom-left"
      })
      return
    }

    if (!selectedMetrics || selectedMetrics.length === 0) {
      toast.error("Please select at least one metric", {
        position: "bottom-left"
      })
      return
    }

    dispatch(
      exportGroupedAnalyticsAction({
        startDate,
        endDate,
        startDate2: isCompare ? startDate2 : "",
        endDate2: isCompare ? endDate2 : "",
        metrics: selectedMetrics,
        raffleAccess,
        employeeClass,
        userStatus,
        awolDays,
        platformUser,
        restaurant,
        usersLoginStatus
      })
    )
  }

  useEffect(() => {
    if (startDate && endDate && selectedMetrics?.length) {
      dispatch(
        getGroupedAnalyticsAction({
          startDate,
          endDate,
          startDate2,
          endDate2,
          metrics: selectedMetrics,
          raffleAccess,
          employeeClass,
          userStatus,
          awolDays,
          platformUser,
          restaurant,
          usersLoginStatus
        })
      )
    }
  }, [raffleAccess, employeeClass, userStatus, platformUser, restaurant, usersLoginStatus])

  const filteredMetrics = metricsOptions?.filter((metric) =>
    metric?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  )

  const open = Boolean(anchorEl)
  const id = open ? "metrics-popover" : undefined

  const dateOpen = Boolean(dateAnchorEl)
  const dateId = dateOpen ? "date-popover" : undefined

  const itemsPerPage = 12
  const totalPages = Math.ceil((groupedAnalytics?.length || 0) / itemsPerPage)

  const paginatedData = groupedAnalytics?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Header title="Grouped Analytics" back={true} breadCrumbs={breadCrumbs}>
          <AppButton sx={{ marginRight: "10px" }} onClick={handleDateClick}>
            <CalendarMonthIcon htmlColor={theme.palette.aqua.main} className="me-2" />
            Date Range
            <KeyboardArrowDownIcon className="ms-2" />
          </AppButton>
          <Popover
            id={dateId}
            open={dateOpen}
            anchorEl={dateAnchorEl}
            onClose={handleDateClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#111111",
                border: "1px solid #333",
                borderRadius: "12px",
                marginTop: "8px",
                boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.4)",
                overflow: "visible"
              }
            }}>
            <AnalyticsDateRangeFilter isDMV={true} />
          </Popover>
          <AppButton sx={{ marginRight: "10px" }} onClick={handleMetricsClick}>
            <img src={tuneIcon} alt="" className="me-2" />
            Metrics
            <KeyboardArrowDownIcon className="ms-2" />
          </AppButton>
          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
            sx={{
              "& .MuiPaper-root": {
                width: "280px",
                backgroundColor: "#161616",
                border: "1px solid #333",
                borderRadius: "8px",
                padding: "16px",
                marginTop: "8px",
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.5)"
              }
            }}>
            <Box>
              <TextField
                fullWidth
                placeholder="Search Metrics"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.white.main }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: "#000",
                    color: theme.palette.white.main,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.white.main
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.white.main
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: theme.palette.aqua.main
                    }
                  }
                }}
                sx={{ mb: 2 }}
              />

              <Box className={styles.metricsScroll}>
                <Box
                  className={styles.metricCard}
                  onClick={() =>
                    handleSelectAll({
                      target: { checked: selectedMetrics.length !== metricsOptions.length }
                    })
                  }>
                  <Checkbox
                    checked={selectedMetrics.length === metricsOptions.length}
                    indeterminate={
                      selectedMetrics.length > 0 && selectedMetrics.length < metricsOptions.length
                    }
                    onChange={handleSelectAll}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      color: theme.palette.white.main,
                      "&.Mui-checked": { color: theme.palette.aqua.main },
                      "&.MuiCheckbox-indeterminate": { color: theme.palette.aqua.main }
                    }}
                  />
                  <Typography sx={{ color: theme.palette.white.main, fontSize: "14px" }}>
                    Select All
                  </Typography>
                </Box>
                {filteredMetrics?.map((metric) => (
                  <Box
                    className={styles.metricCard}
                    key={metric.value}
                    onClick={() => handleMetricToggle(metric.value)}>
                    <Checkbox
                      checked={selectedMetrics.includes(metric.value)}
                      onChange={() => handleMetricToggle(metric.value)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: theme.palette.white.main,
                        "&.Mui-checked": { color: theme.palette.aqua.main }
                      }}
                    />
                    <Typography color={theme.palette.white.main} variant="body2Regular">
                      {metric.label}
                    </Typography>
                    {metric.value === "awol_users" && selectedMetrics.includes(metric.value) && (
                      <>
                        <AppTextField
                          type="number"
                          value={awolDays}
                          onChange={(e) => setAwolDays(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ min: 1 }}
                          sx={{
                            ml: 2,
                            width: "70px",
                            "& .MuiInputBase-input": {
                              padding: "4px 14px",
                              color: theme.palette.white.main,
                              fontSize: "13px"
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.white.main
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.white.main
                            }
                          }}
                        />
                        <Typography>Days</Typography>
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Popover>
          <AppButton onClick={handleGenerateReport} disabled={groupedAnalyticsLoading}>
            <img src={reloadIcon} alt="" className="me-2" />
            Generate Report
          </AppButton>
          <AppButton sx={{ marginLeft: "10px" }} onClick={handleExport}>
            <FileDownloadIcon sx={{ color: theme.palette.aqua.main }} className="me-2" />
            Export
          </AppButton>
        </Header>
      </Grid>
      <Grid container item xs={12}>
        <Grid item xs={12}>
          <AnalyticsTable
            selectedMetrics={appliedMetrics}
            allMetrics={metricsOptions}
            data={paginatedData}
            loading={groupedAnalyticsLoading}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            isCompare={isCompare}
          />
        </Grid>
        {appliedMetrics?.length > 0 && (
          <Grid item xs={12}>
            <AppPagination
              onPageChange={setCurrentPage}
              totalPages={totalPages}
              currentPage={currentPage}
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export default GroupedAnalytics
